import React, { useCallback, useEffect, useState } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  TablePagination,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Divider,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { ButtonBase } from "@mui/material";
import { IconEdit, IconTrash, IconPlus } from "@tabler/icons-react";
import MainCard from "ui-component/cards/MainCard";
import SearchSection from "layout/MainLayout/Header/SearchSection";

const EditButton = styled(ButtonBase)(({ theme }) => ({
  color: theme.palette.secondary.dark,
  "&:hover": {
    background: theme.palette.secondary.dark,
    color: theme.palette.secondary.light,
  },
  width: 40,
  height: 40,
  borderRadius: 10,
  cursor: "pointer",
}));

const DeleteButton = styled(ButtonBase)(({ theme }) => ({
  color: theme.palette.error.dark,
  "&:hover": {
    background: theme.palette.error.dark,
    color: theme.palette.error.light,
  },
  width: 40,
  height: 40,
  borderRadius: 12,
  cursor: "pointer",
}));

const AddButton = styled(ButtonBase)(({ theme }) => ({
  color: theme.palette.info.dark,
  "&:hover": {
    background: theme.palette.secondary.dark,
    color: theme.palette.secondary.light,
  },
  align: "center",
  gap: 8,
  width: 150,
  height: 40,
  borderRadius: 10,
  display: "flex",
  cursor: "pointer",
}));

const NeighborhoodsList = () => {
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [page, setPage] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentNeighborhood, setCurrentNeighborhood] = useState({ name: "" });
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const fetchNeighborhoods = useCallback(() => {
    const searchText = searchTerm !== "" ? `&contains=${searchTerm}` : "";
    fetch(
      `/backend/neighborhoods/?page=${
        page + 1
      }&page_size=${rowsPerPage}${searchText}`
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error("Falha ao carregar os bairros");
        }
        return response.json();
      })
      .then((data) => {
        setNeighborhoods(data.results);
        setTotalRecords(data.count);
      });
  }, [page, rowsPerPage, searchTerm]);

  useEffect(() => {
    fetchNeighborhoods();
  }, [fetchNeighborhoods, page, rowsPerPage, searchTerm]);

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleEdit = (neighborhood) => {
    setCurrentNeighborhood(neighborhood);
    setIsEditing(true);
    setOpenModal(true);
  };

  const handleAdd = () => {
    setCurrentNeighborhood({ name: "", state: "" });
    setIsEditing(false);
    setOpenModal(true);
  };

  const handleCloseModal = () => setOpenModal(false);

  const handleSave = () => {
    const method = isEditing ? "PUT" : "POST";
    const url = isEditing
      ? `/backend/neighborhoods/${currentNeighborhood.id}/`
      : "/backend/neighborhoods/";

    fetch(url, {
      method: method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(currentNeighborhood),
    })
      .then((response) => {
        if (response.ok) return response.json();
        throw new Error("Erro ao salvar bairro");
      })
      .then((data) => {
        if (isEditing) {
          setNeighborhoods((prevNeighborhoods) =>
            prevNeighborhoods.map((neighborhood) =>
              neighborhood.id === data.id ? data : neighborhood
            )
          );
        } else {
          setNeighborhoods((prevNeighborhoods) => [...prevNeighborhoods, data]);
        }
        alert("Bairro salvo com sucesso!");
        setOpenModal(false);
        fetchNeighborhoods();
      })
      .catch((error) => alert("Erro: " + error.message));
  };

  const handleDelete = (neighborhoodId) => {
    if (window.confirm("Você tem certeza que deseja excluir este bairro?")) {
      fetch(`/backend/neighborhoods/${neighborhoodId}/`, {
        method: "DELETE",
      })
        .then((response) => {
          if (response.ok) {
            setNeighborhoods((prevNeighborhoods) =>
              prevNeighborhoods.filter(
                (neighborhood) => neighborhood.id !== neighborhoodId
              )
            );
            alert("Bairro excluído com sucesso!");
            fetchNeighborhoods();
          } else {
            throw new Error("Erro ao excluir bairro");
          }
        })
        .catch((error) => alert("Erro: " + error.message));
    }
  };

  return (
    <div className="w-full overflow-auto">
      <MainCard
        title="Cadastro de bairros"
        actions={[
          <AddButton onClick={handleAdd}>
            <IconPlus stroke={1.5} size="1.1rem" sx={{ mr: 5 }} />
            Adicionar bairro
          </AddButton>,
        ]}
      >
        <SearchSection onSearchChange={setSearchTerm} />
        <Divider sx={{ my: 2 }} />
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {neighborhoods.map((neighborhood) => (
              <TableRow key={neighborhood.id}>
                <TableCell>{neighborhood.name}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Editar">
                    <EditButton onClick={() => handleEdit(neighborhood)}>
                      <IconEdit stroke={1.5} size="1.3rem" />
                    </EditButton>
                  </Tooltip>
                  <Tooltip title="Excluir">
                    <DeleteButton onClick={() => handleDelete(neighborhood.id)}>
                      <IconTrash stroke={1.5} size="1.3rem" />
                    </DeleteButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </MainCard>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={totalRecords}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      <Dialog open={openModal} onClose={handleCloseModal}>
        <DialogTitle>
          {isEditing ? "Editar Bairro" : "Adicionar Bairro"}
        </DialogTitle>
        <DialogContent sx={{ minWidth: 400 }}>
          <TextField
            label="Nome"
            value={currentNeighborhood.name}
            onChange={(e) =>
              setCurrentNeighborhood({
                ...currentNeighborhood,
                name: e.target.value,
              })
            }
            fullWidth
            sx={{ mt: 2, mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="secondary">
            Cancelar
          </Button>
          <Button onClick={handleSave} color="primary">
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default NeighborhoodsList;
